import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { IntervalRef, timerUtils } from '../../../../../utilities/timer';
import { checkSumChipTanCode } from './checkSumChipTanCode';

const BG_COLOR = '#000';
const BAR_COLOR = '#FFF';
const ASPECT_RATIO = 0.45;
const INITIAL_WIDTH = 240;
// The hexadecimal digits mapped to binary, in reversed order
const bitsMap: Record<string, number[]> = {
  '0': [0, 0, 0, 0, 0],
  '1': [0, 1, 0, 0, 0],
  '2': [0, 0, 1, 0, 0],
  '3': [0, 1, 1, 0, 0],
  '4': [0, 0, 0, 1, 0],
  '5': [0, 1, 0, 1, 0],
  '6': [0, 0, 1, 1, 0],
  '7': [0, 1, 1, 1, 0],
  '8': [0, 0, 0, 0, 1],
  '9': [0, 1, 0, 0, 1],
  A: [0, 0, 1, 0, 1],
  B: [0, 1, 1, 0, 1],
  C: [0, 0, 0, 1, 1],
  D: [0, 1, 0, 1, 1],
  E: [0, 0, 1, 1, 1],
  F: [0, 1, 1, 1, 1],
};

export type UseChipTanFlicker = {
  code: string;
  canvasRef: RefObject<HTMLCanvasElement>;
  containerRef: RefObject<HTMLDivElement>;
};

export const useChipTanFlicker = ({ code, canvasRef, containerRef }: UseChipTanFlicker) => {
  const bitarray = useRef<number[][]>([]);
  const halfbyteid = useRef<number>(0);
  const clock = useRef<number>(1);
  const delay = useRef<number>(50);
  const width = useRef<number>(INITIAL_WIDTH);
  const height = useRef<number>(INITIAL_WIDTH * ASPECT_RATIO);
  const [intervalRef, setIntervalRef] = useState<IntervalRef | null>(null);
  const [isVertical, setIsVertical] = useState<boolean>(false);

  const getCanvasWidth = useCallback(() => {
    if (isVertical && containerRef.current) {
      // The canvas should take up the container's full width in vertical mode
      return containerRef.current.offsetWidth;
    }

    return width.current;
  }, [containerRef, isVertical]);

  const getCanvasHeight = useCallback(
    () => (isVertical ? width.current : height.current),
    [isVertical]
  );

  const reset = useCallback(() => {
    halfbyteid.current = 0;
    clock.current = 1;
  }, []);

  const setupBitarray = useCallback(() => {
    if (!code) return;

    const checkedCode = checkSumChipTanCode(code);
    const codeWithStart = `0FFF${checkedCode}`;

    bitarray.current = [];
    for (let i = 0; i < codeWithStart.length; i += 2) {
      bitarray.current[i] = bitsMap[codeWithStart[i + 1]];
      bitarray.current[i + 1] = bitsMap[codeWithStart[i]];
    }
  }, [code]);

  const fillCanvas = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');

    canvasRef.current.width = getCanvasWidth();
    canvasRef.current.height = getCanvasHeight();

    if (!ctx) return;
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, getCanvasWidth(), getCanvasHeight());
  }, [canvasRef, getCanvasHeight, getCanvasWidth]);

  const step = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');

    const margin = 7;
    const barContainerWidth = getCanvasWidth() / 5;
    const barContainerHeight = getCanvasHeight() / 5;
    let barWidth = barContainerWidth - margin * 2;
    let barHeight = getCanvasHeight() - margin * 2;

    if (isVertical) {
      barWidth = getCanvasWidth() - margin * 2;
      barHeight = barContainerHeight - margin * 2;
    }

    bitarray.current[halfbyteid.current][0] = clock.current;

    if (!ctx) return;
    for (let i = 0; i < 5; i++) {
      let x = i * barContainerWidth + margin;
      let y = margin;

      if (isVertical) {
        x = margin;
        y = i * barContainerHeight + margin;
      }

      if (bitarray.current[halfbyteid.current][i] === 1) {
        ctx.fillStyle = BAR_COLOR;
      } else {
        ctx.fillStyle = BG_COLOR;
      }

      ctx.fillRect(x, y, barWidth, barHeight);
    }

    clock.current--;

    if (clock.current < 0) {
      clock.current = 1;

      halfbyteid.current++;

      if (halfbyteid.current >= bitarray.current.length) {
        halfbyteid.current = 0;
      }
    }
  }, [canvasRef, getCanvasHeight, getCanvasWidth, isVertical]);

  const stopFlicker = useCallback(() => {
    if (intervalRef) {
      clearInterval(intervalRef);
      setIntervalRef(null);
    }
  }, [intervalRef]);

  const startFlicker = useCallback(() => {
    if (!intervalRef) {
      setIntervalRef(timerUtils.setInterval(step, delay.current));
    }

    return () => {
      stopFlicker();
    };
  }, [intervalRef, step, stopFlicker]);

  useEffect(() => {
    fillCanvas();
    setupBitarray();
    reset();
  }, [fillCanvas, reset, setupBitarray]);

  const scaleUp = useCallback(() => {
    if (width.current < 1000) {
      width.current += 50;
      height.current += 50 * ASPECT_RATIO;
      fillCanvas();
    }
  }, [fillCanvas]);

  const scaleDown = useCallback(() => {
    if (width.current > 150) {
      width.current -= 50;
      height.current -= 50 * ASPECT_RATIO;
      fillCanvas();
    }
  }, [fillCanvas]);

  const rotate = useCallback(() => {
    setIsVertical(!isVertical);
    fillCanvas();
  }, [isVertical, fillCanvas]);

  const speedUp = useCallback(() => {
    if (delay.current > 50) {
      delay.current -= 50;
      stopFlicker();
      startFlicker();
    }
  }, [startFlicker, stopFlicker]);

  const slowDown = useCallback(() => {
    if (delay.current < 500) {
      delay.current += 50;
      stopFlicker();
      startFlicker();
    }
  }, [startFlicker, stopFlicker]);

  return {
    scaleUp,
    scaleDown,
    speedUp,
    slowDown,
    rotate,
    startFlicker,
    stopFlicker,
  };
};
